
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface OwlProps {
  className?: string;
}

export const Owl: React.FC<OwlProps> = ({ className }) => {
  const [isSpreadingWings, setIsSpreadingWings] = useState(false);
  const [isScratching, setIsScratching] = useState(false);
  const [isExcited, setIsExcited] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    // Watch for sign in form activity
    const handleSignInActivity = () => {
      setIsExcited(true);
      // Reset excitement after a bit
      setTimeout(() => setIsExcited(false), 3000);
    };

    const signInForm = document.querySelector('form');
    if (signInForm) {
      signInForm.addEventListener('focusin', handleSignInActivity);
      return () => signInForm.removeEventListener('focusin', handleSignInActivity);
    }
  }, []);

  useEffect(() => {
    // Random wing spreading
    const spreadWings = () => {
      if (!isExcited && Math.random() < 0.05) { // 5% chance if not excited
        setIsSpreadingWings(true);
        setTimeout(() => setIsSpreadingWings(false), 1200);
      }
    };

    // Random scratching
    const scratch = () => {
      if (!isExcited && Math.random() < 0.08) { // 8% chance if not excited
        setIsScratching(true);
        setTimeout(() => setIsScratching(false), 1000);
      }
    };

    const wingInterval = setInterval(spreadWings, 10000);
    const scratchInterval = setInterval(scratch, 8000);

    return () => {
      clearInterval(wingInterval);
      clearInterval(scratchInterval);
    };
  }, [isExcited]);

  const handleClick = () => {
    if (!isSpreadingWings && !isScratching && !isExcited) {
      if (Math.random() < 0.5) {
        setIsSpreadingWings(true);
        setTimeout(() => setIsSpreadingWings(false), 1200);
      } else {
        setIsScratching(true);
        setTimeout(() => setIsScratching(false), 1000);
      }
    }
  };

  return (
    <div 
      className={`owl-container ${className || ''}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Glow effect */}
      <motion.div 
        className="absolute inset-0 rounded-full bg-indigo-500/20 blur-xl"
        animate={{ 
          opacity: isHovering || isExcited ? [0.3, 0.6, 0.3] : 0.2,
          scale: isHovering || isExcited ? [1, 1.1, 1] : 1 
        }}
        transition={{
          duration: 2,
          repeat: isHovering || isExcited ? Infinity : 0,
          ease: "easeInOut"
        }}
      />
      
      {/* Branch */}
      <motion.div 
        className="branch absolute h-2 w-24 bg-gradient-to-r from-amber-800 to-amber-600 rounded-md z-10"
        style={{ 
          bottom: '10%', 
          left: '50%', 
          transform: 'translateX(-50%)',
          filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.3))'
        }}
      />
      
      {/* Owl */}
      <motion.div 
        className={`owl relative ${isSpreadingWings ? 'spread-wings' : ''} ${isExcited ? 'excited' : ''}`}
        onClick={handleClick}
        role="button"
        aria-label="Cute interactive owl on a branch"
        animate={isHovering ? { y: [0, -5, 0] } : {}}
        transition={{ 
          duration: 2, 
          repeat: isHovering ? Infinity : 0,
          ease: "easeInOut" 
        }}
      >
        {/* Wings */}
        <motion.div 
          className="owl-wing left absolute w-14 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full"
          style={{ 
            left: '-20px', 
            top: '5px',
            transformOrigin: 'right center',
            borderTopLeftRadius: '70%',
            borderBottomLeftRadius: '50%',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
          }}
          animate={isSpreadingWings ? { rotate: -40 } : {}}
        />
        <motion.div 
          className="owl-wing right absolute w-14 h-20 bg-gradient-to-bl from-gray-300 to-gray-400 rounded-full"
          style={{ 
            right: '-20px', 
            top: '5px',
            transformOrigin: 'left center',
            borderTopRightRadius: '70%',
            borderBottomRightRadius: '50%',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
          }}
          animate={isSpreadingWings ? { rotate: 40 } : {}}
        />
        
        {/* Body */}
        <motion.div 
          className="owl-body w-24 h-28 bg-gradient-to-b from-gray-200 to-gray-300 rounded-full"
          style={{ 
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
          }}
          animate={isExcited ? { 
            y: [0, -5, 0, -5, 0],
            rotate: [0, 3, 0, -3, 0]
          } : {}}
          transition={{ 
            duration: isExcited ? 0.5 : 0.3,
            ease: "easeInOut" 
          }}
        />
        
        {/* Face */}
        <div className="owl-face absolute inset-0 flex flex-col items-center justify-center">
          <div className="eyes flex gap-4">
            <motion.div 
              className="owl-eye-area left bg-white w-8 h-8 rounded-full flex items-center justify-center"
              animate={isExcited ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <motion.div 
                className="owl-eye bg-black w-4 h-4 rounded-full"
                animate={isExcited ? { scale: [1, 1.2, 1] } : {}}
              />
            </motion.div>
            <motion.div 
              className="owl-eye-area right bg-white w-8 h-8 rounded-full flex items-center justify-center"
              animate={isExcited ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <motion.div 
                className="owl-eye bg-black w-4 h-4 rounded-full"
                animate={isExcited ? { scale: [1, 1.2, 1] } : {}}
              />
            </motion.div>
          </div>
          <motion.div 
            className="owl-beak mt-1 w-4 h-4 bg-gradient-to-b from-amber-400 to-amber-500"
            style={{ 
              clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))'
            }}
          />
        </div>
        
        {/* Legs */}
        <div className="owl-legs absolute bottom-0 left-1/2 transform -translate-x-1/2 flex gap-2">
          <motion.div 
            className={`owl-leg w-1 h-4 bg-amber-700 ${isScratching ? 'scratching' : ''}`}
            animate={isScratching ? { 
              rotate: [0, 20, 0, 20, 0], 
              y: [0, -2, 0, -2, 0] 
            } : {}}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <div className="owl-foot w-2 h-1 bg-amber-800 -ml-0.5" />
          </motion.div>
          <motion.div className="owl-leg w-1 h-4 bg-amber-700">
            <div className="owl-foot w-2 h-1 bg-amber-800 -ml-0.5" />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};
